// Tree follow-up after a server-side recalc: reload the voyage, move the
// selection from the archived route to the new one, keep the map on it.
import { useCallback } from "react";
import { treeSelectionActions } from "@/features/routeTree/state/VesselTreeState.jsx";
import { ESTIMATED_START, ESTIMATED_START_MESSAGE } from "@/features/route/recalcStart";

export default function useRecalcCompletion({
  treeDispatch,
  refetchPlanRoutes,
  recenterToRoute,
  loadRoutesForVoyage,
  addNotification,
}) {
  return useCallback(
    async ({ target, route, archivedRouteId }) => {
      const newRouteId = Number(route?.id);
      const voyageId = Number(route?.voyageId ?? target?.voyage?.id);
      // Tree selection ids are strict numbers.
      const rawVesselId = target?.vessel?.id ?? target?.voyage?.vesselId;
      const vesselId = Number.isFinite(Number(rawVesselId)) ? Number(rawVesselId) : null;
      const label = route?.name ?? newRouteId;

      // loadRoutesForVoyage swallows fetch errors and returns []; without the
      // new route in the list, moving the selection would dangle.
      const routes = Number.isFinite(voyageId)
        ? await loadRoutesForVoyage?.(voyageId, { forceRefresh: true })
        : [];
      const reloaded = (routes ?? []).some((r) => Number(r?.id) === newRouteId);
      if (!reloaded) {
        // The recalc did persist; at least drop the archived geometry from the map.
        refetchPlanRoutes?.();
        addNotification?.({
          message: `Route recalculated (${label}), but the route list could not be reloaded — reopen the voyage.`,
          type: "warning",
        });
        return;
      }

      // ensureRouteSelected is a no-op without a vessel id; then keep the
      // current selection instead of deselecting into nothing.
      if (vesselId != null) {
        if (archivedRouteId != null) {
          treeDispatch?.(treeSelectionActions.deselectRoute(Number(archivedRouteId)));
        }
        treeDispatch?.(
          treeSelectionActions.ensureRouteSelected(vesselId, voyageId, newRouteId),
        );
      }
      refetchPlanRoutes?.();
      recenterToRoute?.(newRouteId);
      addNotification?.({
        message: `Route recalculated with current weather: ${label}`,
        type: "success",
      });
      if (route?.summary?.start_source === ESTIMATED_START) {
        addNotification?.({ message: ESTIMATED_START_MESSAGE, type: "info" });
      }
    },
    [treeDispatch, refetchPlanRoutes, recenterToRoute, loadRoutesForVoyage, addNotification],
  );
}
